/**
 * MODULE: domain/pages/frontend/components/editor/PipelineSnapshotConfigModal.tsx
 * PURPOSE: Configure pipeline snapshot block display options
 */

import { Button, Checkbox, Label, Modal, ModalBody, ModalFooter, ModalHeader, TextInput } from 'flowbite-react';
import { useEffect, useState } from 'react';
import type { PipelineSnapshotBlockContent } from '../../utils/pagePipelineSnapshotUtils';

type PipelineSnapshotConfigModalProps = {
  open: boolean;
  initial: PipelineSnapshotBlockContent;
  onClose: () => void;
  onSubmit: (content: PipelineSnapshotBlockContent) => void;
};

export function PipelineSnapshotConfigModal({
  open,
  initial,
  onClose,
  onSubmit,
}: PipelineSnapshotConfigModalProps) {
  const [title, setTitle] = useState(initial.title);
  const [showKpis, setShowKpis] = useState(initial.show_kpis);
  const [showFunnel, setShowFunnel] = useState(initial.show_funnel);

  useEffect(() => {
    if (!open) return;
    setTitle(initial.title);
    setShowKpis(initial.show_kpis);
    setShowFunnel(initial.show_funnel);
  }, [initial, open]);

  const handleSubmit = () => {
    onSubmit({
      title: title.trim() || initial.title,
      show_kpis: showKpis,
      show_funnel: showFunnel,
    });
    onClose();
  };

  return (
    <Modal show={open} onClose={onClose} size="md" data-testid="pages-pipeline-snapshot-modal">
      <ModalHeader>Pipeline snapshot</ModalHeader>
      <ModalBody>
        <div className="space-y-4">
          <div>
            <Label htmlFor="pages-pipeline-snapshot-title">Title</Label>
            <TextInput
              id="pages-pipeline-snapshot-title"
              data-testid="pages-pipeline-snapshot-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Pipeline snapshot"
            />
          </div>
          <fieldset className="space-y-2">
            <legend className="mb-1 text-sm font-medium text-gray-900 dark:text-white">Show sections</legend>
            <div className="flex items-center gap-2">
              <Checkbox
                id="pages-pipeline-snapshot-show-kpis"
                data-testid="pages-pipeline-snapshot-show-kpis"
                checked={showKpis}
                onChange={(e) => setShowKpis(e.target.checked)}
              />
              <Label htmlFor="pages-pipeline-snapshot-show-kpis">KPI summary</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="pages-pipeline-snapshot-show-funnel"
                data-testid="pages-pipeline-snapshot-show-funnel"
                checked={showFunnel}
                onChange={(e) => setShowFunnel(e.target.checked)}
              />
              <Label htmlFor="pages-pipeline-snapshot-show-funnel">Stage funnel</Label>
            </div>
          </fieldset>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="gray" onClick={onClose}>
          Cancel
        </Button>
        <Button data-testid="pages-pipeline-snapshot-submit" onClick={handleSubmit}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
}
